import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Star, Shield, Droplets, Sparkles } from "lucide-react";
import { Seo } from "../components/Seo";
import { Hero } from "../components/site/Hero";
import { Reveal, SectionHeader } from "../components/site/Reveal";
import { Faq } from "../components/site/Faq";
import { FEATURED_SERVICES, BRAND, FAQ } from "../lib/content";

export default function Home() {
  return (
    <>
      <Seo title={null} description={BRAND.description} path="/" />
      <Hero />

      {/* Trust band */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
          <p className="overline">{BRAND.training}</p>
          <span className="text-gray-700">·</span>
          {BRAND.productBrands.map((b, i) => (
            <span key={i} className="font-display font-bold text-silver tracking-wider">{b}</span>
          ))}
        </div>
      </section>

      {/* Featured services */}
      <section data-testid="featured-services" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            overline="Featured Services"
            title="Detailing, correction & protection."
            description="Choose from our premium service tiers — explore each page for full inclusions and pricing."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-14">
            {FEATURED_SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 4) * 0.06}>
                <Link to={s.href} data-testid={`featured-${s.id}`} className={`block matte p-6 h-full group hover:border-silver/40 transition-all duration-300 ${s.recommended ? "silver-glow" : ""}`}>
                  {s.recommended && (
                    <span className="inline-block bg-silver text-black text-[9px] font-bold uppercase tracking-widest px-2 py-1 mb-3">Recommended</span>
                  )}
                  <h3 className="font-display text-lg font-bold tracking-tight text-white leading-snug">{s.name}</h3>
                  <p className="font-mono-tech text-silver text-sm mt-2">{s.price}</p>
                  <p className="text-gray-400 text-sm font-light mt-4 leading-relaxed min-h-[4.5rem]">{s.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-white text-xs font-bold uppercase tracking-widest mt-5 group-hover:text-silver transition-colors">
                    View Details <ChevronRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="overline">About SPECA</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-5 leading-[1.05]">
              Driven by passion. <span className="text-silver">Defined by precision.</span>
            </h2>
            <p className="text-gray-400 font-light mt-5 leading-relaxed">
              Based in {BRAND.location}, SPECA Detailing provides premium automotive detailing, paint correction and ceramic protection services for customers who value quality workmanship and attention to detail. Professionally trained by XEE Detailing, we combine careful techniques with trusted professional products.
            </p>
            <div className="mt-8 flex gap-4">
              <Link to="/about" className="text-white border border-white/20 hover:border-white px-6 py-3 uppercase tracking-widest text-xs font-bold transition-colors">Read Our Story</Link>
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            {[{ i: Sparkles, t: "Precision" }, { i: Shield, t: "Protection" }, { i: Droplets, t: "Quality" }, { i: Star, t: "Trusted Brands" }].map((x, i) => (
              <div key={i} className="matte p-6">
                <x.i className="text-silver mb-3" size={22} />
                <p className="font-display font-bold text-white">{x.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Faq items={FAQ.slice(0, 4)} />

      {/* CTA band */}
      <section className="py-20 border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Ready to book your detail?</h2>
          <p className="text-gray-400 font-light mt-5 max-w-2xl mx-auto">Mobile detailing available across Melbourne. Paint correction and ceramic coating completed in-house.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/contact" className="bg-silver hover:bg-white text-black px-8 py-4 uppercase tracking-widest text-sm font-bold">Book Now</Link>
            <Link to="/services" className="border border-white/20 hover:border-white text-white px-8 py-4 uppercase tracking-widest text-sm font-bold">View All Services</Link>
          </div>
        </div>
      </section>
    </>
  );
}
